import {
  constructUrl,
  getCanonicalPathItemPath,
  normalizePathItemType
} from "@pathwright/features/src/modules/utils/urls"

// These function are used by route `onEnter` handlers to map route state to library/resource/path store state

const getSection = (section) => {
  const defaultSection = "about"
  const previousSection = App.getStore("resource").previous("section")
  const sections = ["about", "path", "community"]

  if (!sections.includes(section)) {
    return previousSection || defaultSection
  }
  return section
}

export const loadResource = async function (nextState, replaceState /* ? */) {
  const { resourceSlug, cohortId, section } = nextState.match.params
  const resourceStore = window.App.getStore("resource")
  const result = await resourceStore.action.selectResourceBySlug(
    resourceSlug,
    cohortId,
    getSection(section)
  ).promise

  const { resource } = resourceStore.getState()

  // The resource slug in the URL may not match the resource.slug.
  // This could be due to the resource slug being incomplete
  // (may just be the resource.id).
  // NOTE: this same behavior is handled by the backend, but sometimes
  // the client uses internal links that only include the resource.id.
  // These are the cases this client-side solution addresses.
  if (
    resource &&
    resource.slug !== resourceSlug &&
    resource.id == resourceSlug &&
    replaceState
  ) {
    // For semantics, lets redirect to the <resource_slug>-<id> slug
    // when the resource slug doesn't match.
    if (resource.id == resourceSlug) {
      const pathname = location.pathname.replace(
        `/${resourceSlug}/`,
        `/${resource.slug}/`
      )
      return replaceState({
        ...location,
        pathname
      })
    }
  }

  return result
}

const pathMatchesCohort = (path, offering, expectedCohortId) => {
  if (!path || !offering) return false
  if (path.offering_id !== offering.id) return false
  if (
    expectedCohortId != null &&
    String(offering.id) !== String(expectedCohortId)
  ) {
    return false
  }
  return true
}

// Wait for the path store to hold the path for this route's cohort.
// Ignore path changes for a different cohort (e.g. parent collection while a
// child /step/ enter is still pending) so we do not select the child item
// against the wrong path and spuriously set step_404 / lesson_404.
const loadPath = (expectedCohortId) => {
  return new Promise((resolve) => {
    const pathStore = App.getStore("path")

    const tryResolve = () => {
      const { offering } = App.getStore("resource").getState()
      const { path } = pathStore.getState()
      if (pathMatchesCohort(path, offering, expectedCohortId)) {
        resolve(path)
        return true
      }
      // Resource store already moved to another cohort — abandon this wait.
      if (
        expectedCohortId != null &&
        offering &&
        String(offering.id) !== String(expectedCohortId)
      ) {
        resolve(null)
        return true
      }
      return false
    }

    if (tryResolve()) return

    const onPathChange = () => {
      if (tryResolve()) {
        App.stopListening(pathStore, "change:path", onPathChange)
      }
    }

    App.listenTo(pathStore, "change:path", onPathChange)
  })
}

const clearStaleMissingPathItem = () => {
  App.getStore("step")._mutate({ step_404: null })
  App.getStore("path")._mutate({ lesson_404: null })
}

export const loadResourcePath = function (nextState, replaceState /* ? */) {
  const { cohortId, pathItemSourceId } = nextState.match.params
  return loadResource(nextState, replaceState).then(() => {
    // Path-level navigations (no step/lesson in the URL) should not keep a
    // MissingModal from a prior child-path enter that is still resolving.
    if (!pathItemSourceId) {
      clearStaleMissingPathItem()
    }
    return loadPath(cohortId)
  })
}

// True when this route enter should not select a path item — the user already
// left the URL, or the resource/path store now belong to a different cohort.
// Without this, a pending child /step/ enter can call setSelectedStepByID after
// the user opened the parent collection, look up the child id on the parent
// path, set step_404, and show MissingModal with parent pathId + child item id.
export const isStalePathItemNavigation = (nextState) => {
  const enteredPathname = nextState.location?.pathname
  if (enteredPathname && window.location.pathname !== enteredPathname) {
    return true
  }

  const { cohortId } = nextState.match.params
  const { offering } = App.getStore("resource").getState()
  const { path } = App.getStore("path").getState()
  return !pathMatchesCohort(path, offering, cohortId)
}

// A /step/ or /lesson/ URL can point at an item of a different type (or at a
// row id rather than sync source id). Redirect to the canonical path before
// selecting the item so the step renderer never receives a resource.
const redirectIfPathItemUrlMismatch = (nextState, replaceState) => {
  const { pathItemSourceId, pathItemType } = nextState.match.params
  const sourceId = parseInt(pathItemSourceId, 10)
  const expectedUrlType = normalizePathItemType(pathItemType)
  if (!sourceId || !expectedUrlType) return false

  const pathStore = window.App.getStore("path")
  const item =
    pathStore.request.findItem(sourceId) ||
    pathStore.request.getPathItemById(sourceId)
  if (!item) return false

  const itemType = normalizePathItemType(
    item.get("item_type") || item.get("type")
  )
  const itemSourceId = Number(item.get("sync_source_id"))
  if (!itemType || !itemSourceId) return false
  if (itemType === expectedUrlType && itemSourceId === sourceId) return false

  // The path load is async. Skip the replace if the user already left this URL,
  // and skip selecting the mismatched item.
  if (isStalePathItemNavigation(nextState)) {
    return true
  }

  const { resourceSlug, cohortId, section } = nextState.match.params
  const base = `/library/${resourceSlug}/${cohortId}/${section}/`
  let pathname = constructUrl(
    base,
    getCanonicalPathItemPath({ type: itemType, sourceId: itemSourceId })
  )

  // Keep step child routes (discussion, notes) when only the source id changes.
  if (itemType === "step" && expectedUrlType === "step") {
    const currentItemUrl = constructUrl(base, `step/${sourceId}/`)
    const remainder = (nextState.location?.pathname || "").slice(
      currentItemUrl.length
    )
    if (remainder) pathname = constructUrl(pathname, remainder)
  }

  if (typeof replaceState === "function") {
    replaceState({
      pathname,
      search: nextState.location?.search,
      hash: nextState.location?.hash
    })
  }
  return true
}

export const loadResourcePathStep = function (nextState, replaceState) {
  const { pathItemSourceId } = nextState.match.params
  return loadResourcePath(nextState, replaceState).then(() => {
    if (isStalePathItemNavigation(nextState)) return true
    if (redirectIfPathItemUrlMismatch(nextState, replaceState)) return true
    window.App.getStore("step").action.setSelectedStepByID(pathItemSourceId)
    return false
  })
}

export const loadResourcePathLesson = function (nextState, replaceState) {
  const { pathItemSourceId } = nextState.match.params
  return loadResourcePath(nextState, replaceState).then(() => {
    if (isStalePathItemNavigation(nextState)) return true
    if (redirectIfPathItemUrlMismatch(nextState, replaceState)) return true
    window.App.getStore("path").action.selectGroupByID(pathItemSourceId)
    return false
  })
}
